import React from "react";

const fineGrid = `data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 60 0 L 0 0 0 60' fill='none' stroke='%23FFFFFF' stroke-opacity='0.08' stroke-width='0.5'/%3E%3C/svg%3E`;

// Vanishing point at horizontal center, 40% down
const VP = { x: 720, y: 360 };

// Perspective floor: radial lines from VP to bottom edge
const radialXs = [0, 80, 160, 240, 320, 400, 480, 560, 640, 720, 800, 880, 960, 1040, 1120, 1200, 1280, 1360, 1440];

// Horizontal parallels — opacities doubled for Mac Retina visibility
const floorLines = [
  { y: 365, x1: 713, x2: 727, op: 0.09 },
  { y: 382, x1: 693, x2: 747, op: 0.11 },
  { y: 409, x1: 655, x2: 785, op: 0.13 },
  { y: 446, x1: 605, x2: 835, op: 0.16 },
  { y: 495, x1: 540, x2: 900, op: 0.18 },
  { y: 554, x1: 461, x2: 979, op: 0.21 },
  { y: 625, x1: 365, x2: 1075, op: 0.24 },
  { y: 706, x1: 259, x2: 1181, op: 0.26 },
  { y: 797, x1: 137, x2: 1303, op: 0.28 },
  { y: 900, x1: 0,   x2: 1440, op: 0.30 },
];

const StaticBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">

      {/* Base */}
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at 55% 45%, #0f1623 0%, #080C10 65%)",
        }}
      />

      {/* Fine blueprint grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url("${fineGrid}")`,
          backgroundSize: "60px 60px",
        }}
      />

      {/* Orange horizon glow */}
      <div
        className="absolute"
        style={{
          left: "50%",
          top: "40%",
          transform: "translate(-50%, -50%)",
          width: "900px",
          height: "400px",
          background: "radial-gradient(ellipse, rgba(240,112,35,0.22) 0%, rgba(240,112,35,0.08) 40%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Blue-navy depth glow — top right */}
      <div
        className="absolute -top-40 -right-40 w-[700px] h-[700px]"
        style={{
          background: "radial-gradient(ellipse, rgba(20,60,140,0.18) 0%, transparent 65%)",
          pointerEvents: "none",
        }}
      />

      {/* SVG geometry layer */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1440 900"
        fill="none"
        preserveAspectRatio="none"
      >
        {/* ── Perspective floor grid ── */}

        {/* Radial lines */}
        {radialXs.map((x) => (
          <line
            key={x}
            x1={VP.x} y1={VP.y}
            x2={x} y2={900}
            stroke="#F07023"
            strokeOpacity={x === 720 ? 0 : 0.16}
            strokeWidth="1"
          />
        ))}

        {/* Horizontal parallels */}
        {floorLines.map((l) => (
          <line
            key={l.y}
            x1={l.x1} y1={l.y}
            x2={l.x2} y2={l.y}
            stroke="#F07023"
            strokeOpacity={l.op}
            strokeWidth="1"
          />
        ))}

        {/* ── Top-area geometry ── */}

        {/* Diagonal accents from top-right */}
        <line x1="1440" y1="0" x2="600" y2={VP.y} stroke="#F07023" strokeOpacity="0.16" strokeWidth="1" />
        <line x1="1440" y1="120" x2="900" y2={VP.y} stroke="#F07023" strokeOpacity="0.10" strokeWidth="1" />

        {/* Dashed measurement bar */}
        <line x1="80" y1="180" x2="500" y2="180" stroke="#F07023" strokeOpacity="0.22" strokeWidth="1" strokeDasharray="8 6" />
        <line x1="80" y1="180" x2="80" y2="196" stroke="#F07023" strokeOpacity="0.35" strokeWidth="1" />
        <line x1="500" y1="180" x2="500" y2="196" stroke="#F07023" strokeOpacity="0.35" strokeWidth="1" />

        {/* ── HUD corner frames ── */}
        <polyline points="0,60 0,0 80,0"       stroke="#F07023" strokeOpacity="0.45" strokeWidth="2" />
        <polyline points="1360,0 1440,0 1440,60"  stroke="#F07023" strokeOpacity="0.45" strokeWidth="2" />
        <polyline points="0,840 0,900 80,900"   stroke="#F07023" strokeOpacity="0.45" strokeWidth="2" />
        <polyline points="1360,900 1440,900 1440,840" stroke="#F07023" strokeOpacity="0.45" strokeWidth="2" />

        {/* ── Targeting reticle ── */}
        <circle cx="1260" cy="220" r="36" stroke="#F07023" strokeOpacity="0.18" strokeWidth="1" />
        <circle cx="1260" cy="220" r="5"  stroke="#F07023" strokeOpacity="0.35" strokeWidth="1" />
        <line x1="1224" y1="220" x2="1296" y2="220" stroke="#F07023" strokeOpacity="0.20" strokeWidth="1" />
        <line x1="1260" y1="184" x2="1260" y2="256" stroke="#F07023" strokeOpacity="0.20" strokeWidth="1" />
        <line x1="1224" y1="218" x2="1224" y2="222" stroke="#F07023" strokeOpacity="0.38" strokeWidth="1.5" />
        <line x1="1296" y1="218" x2="1296" y2="222" stroke="#F07023" strokeOpacity="0.38" strokeWidth="1.5" />

      </svg>

      {/* Edge rails */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#F07023]/35 to-transparent" />
      <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#1e50a0]/40 to-transparent" />
    </div>
  );
};

export default StaticBackground;
